import type { UseSearchStateReturn } from "@workspace/types";
import { useCallback, useState } from "react";

/**
 * Enhanced search state management hook
 *
 * Manages all search-related state including:
 * - Search open/closed state
 * - Search input value
 * - Selected result index for keyboard navigation
 *
 * @returns Search state and control functions
 */
export function useSearchState(): UseSearchStateReturn {
	const [isSearchOpen, setSearchOpen] = useState(false);
	const [searchValue, setSearchValue] = useState("");
	const [selectedSearchIndex, setSelectedSearchIndex] = useState(-1);

	const resetSearchState = useCallback(() => {
		setSearchOpen(false);
		setSearchValue("");
		setSelectedSearchIndex(-1);
	}, []);

	const incrementSearchIndex = useCallback((totalResults: number) => {
		setSelectedSearchIndex((prev) => (prev >= totalResults - 1 ? 0 : prev + 1));
	}, []);

	const decrementSearchIndex = useCallback((totalResults: number) => {
		setSelectedSearchIndex((prev) => (prev <= 0 ? totalResults - 1 : prev - 1));
	}, []);

	const resetSearchIndex = useCallback(() => {
		setSelectedSearchIndex(-1);
	}, []);

	return {
		isSearchOpen,
		searchValue,
		selectedSearchIndex,
		setSearchOpen,
		setSearchValue,
		setSelectedSearchIndex,
		resetSearchState,
		incrementSearchIndex,
		decrementSearchIndex,
		resetSearchIndex,
	};
}
